/**
 * ManagedConnection — one WebPubSub client and its lifecycle.
 *
 * `reconnecting` is the SDK's own recovery in progress: the client is alive and
 * working on it, so nothing above should tear it down. `degraded` means the SDK
 * has stopped trying, which is the only state that warrants replacing it.
 * `disposed` is terminal — a discarded client can still fire `connected`, and
 * without that guard it would resurrect itself into `open`.
 */

import {
  WebPubSubClient,
  WebPubSubJsonReliableProtocol
} from '@azure/web-pubsub-client';

import logger from 'src/utils/logger';

import type { ChatId } from './ChatTransport';
import type { HealthMonitor, HealthStatus } from './connectionHealth';
import { createHealthMonitor } from './connectionHealth';
import type { EncryptionCodec } from './EncryptionCodec';
import type { PubsubEventPayload } from './eventSchema';
import { validatePubsubEvent } from './eventSchema';
import { createFrameReassembler } from './frameReassembly';

/**
 * Socket lifecycle. `reconnecting` is the SDK's own recovery in progress — the client
 * is alive and working on it, so nothing above should tear it down.
 */
export type ManagedConnectionState =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'degraded'
  | 'disposed';

export type ConnectionEventListener = (event: PubsubEventPayload) => void;
export type StateChangeListener = (state: ManagedConnectionState) => void;
export type Unsubscribe = () => void;

/**
 * Resolves the WebPubSub client-access URL, and is re-invoked on EVERY connect attempt
 * — initial and reconnect alike.
 */
export type WsUrlProvider = () => string | Promise<string>;

export interface ManagedConnectionOptions {
  getUrl: WsUrlProvider;
  codec: EncryptionCodec;
  healthMonitor?: HealthMonitor;
}

export interface ManagedConnection {
  readonly id: string;
  start(): Promise<void>;
  stop(): Promise<void>;
  getState(): ManagedConnectionState;
  getConnectionId(): string | undefined;
  getHealth(): HealthStatus;
  subscribe(chatId: ChatId, listener: ConnectionEventListener): Unsubscribe;
  onStateChange(listener: StateChangeListener): Unsubscribe;
  markDegraded(reason?: string): void;
}

let nextId = 1;

export function createManagedConnection(
  opts: ManagedConnectionOptions
): ManagedConnection {
  const id = `mc-${nextId++}`;
  const health = opts.healthMonitor ?? createHealthMonitor();

  let state: ManagedConnectionState = 'idle';
  let connectionId: string | undefined;
  let client: WebPubSubClient | undefined;
  const listeners = new Map<ChatId, Set<ConnectionEventListener>>();
  const stateListeners = new Set<StateChangeListener>();
  const reassembler = createFrameReassembler({
    onEvict: (eviction) => {
      logger.warn('[chat:transport] chunked payload evicted before completing', {
        id,
        ...eviction
      });
    }
  });

  const setState = (next: ManagedConnectionState) => {
    if (state === next) {
      return;
    }
    if (state === 'disposed') {
      return;
    }
    state = next;
    stateListeners.forEach((l) => {
      try {
        l(next);
      } catch (error) {
        logger.error('[chat:transport] state listener threw', {
          id,
          state: next,
          error
        });
      }
    });
  };

  const onConnected = (e: any) => {
    connectionId = e?.connectionId;
    health.markActivity();
    setState('open');
  };

  const onDisconnected = () => {
    health.markFailure('disconnected');
    setState('reconnecting');
  };

  const onStopped = () => {
    health.markFailure('stopped');
    setState('degraded');
  };

  const onServerMessage = ({ message }: any) => {
    health.markActivity();
    const decoded = opts.codec.decode(message);
    if (decoded === undefined) {
      return;
    }
    const assembled = reassembler.accept(decoded);
    if (assembled.kind === 'pending') {
      return;
    }
    if (assembled.kind === 'dropped') {
      logger.warn('[chat:transport] chunked payload dropped', {
        id,
        reason: assembled.reason,
        payloadId: assembled.payloadId
      });
      return;
    }
    const result = validatePubsubEvent(assembled.frame);
    if (!result.ok || !result.data) {
      return;
    }
    listeners.forEach((set) => {
      set.forEach((l) => {
        try {
          l(result.data!);
        } catch {
          /* DELIBERATELY silent, and the only catch here that is: this runs per
           * FRAME (~110 a turn, every chat), so logging would turn one broken
           * subscriber into a flood. A listener that throws here is a bug in the
           * runner, which has its own reporting. */
        }
      });
    });
  };

  return {
    id,

    async start() {
      if (state !== 'idle') {
        return;
      }
      setState('connecting');
      try {
        client = new WebPubSubClient(
          { getClientAccessUrl: async () => opts.getUrl() },
          { protocol: WebPubSubJsonReliableProtocol() }
        );
        client.on('connected', onConnected);
        client.on('disconnected', onDisconnected);
        client.on('stopped', onStopped);
        client.on('server-message', onServerMessage);
        await client.start();
      } catch (err) {
        health.markFailure((err as Error)?.message);
        setState('degraded');
        throw err;
      }
    },

    async stop() {
      if (state === 'disposed') {
        return;
      }
      setState('disposed');
      const dying = client;
      client = undefined;
      listeners.clear();
      reassembler.clear();
      connectionId = undefined;
      try {
        dying?.stop();
      } catch (error) {
        logger.error('[chat:transport] stopping a dying client threw', {
          id,
          error
        });
      }
      if (dying) {
        dying.off('connected', onConnected);
        dying.off('disconnected', onDisconnected);
        dying.off('stopped', onStopped);
        dying.off('server-message', onServerMessage);
        dying.on('connected', () => {
          try {
            dying.stop();
          } catch {}
        });
      }
    },

    getState() {
      return state;
    },

    getConnectionId() {
      return connectionId;
    },

    getHealth() {
      return health.getStatus();
    },

    subscribe(chatId, listener) {
      let set = listeners.get(chatId);
      if (!set) {
        set = new Set();
        listeners.set(chatId, set);
      }
      set.add(listener);
      return () => {
        const current = listeners.get(chatId);
        if (!current) {
          return;
        }
        current.delete(listener);
        if (current.size === 0) {
          listeners.delete(chatId);
        }
      };
    },

    onStateChange(l) {
      stateListeners.add(l);
      return () => stateListeners.delete(l);
    },

    markDegraded(reason) {
      health.markFailure(reason);
      setState('degraded');
    }
  };
}
